import { unsafeCoerce } from 'fp-ts/lib/function';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useBookingForm } from '../../../bookings/util/useBookingForm';
import { BookingFormClient } from '../../../bookings/util/useDisplayAppointment';
import { BookingForm } from '../../AnalyticsContext/TrackingData';
import { logErrorToSentry } from '../../Sentry';
import { useUserData } from '../../UserProvider/UserProvider';
import { useRouter } from '../../hooks/useRouter';
import { api3Call } from '../../schema/api3/client';
import * as api3 from '../../schema/api3/endpoints';
import { useBrowser } from '../../services/BrowserService';
import { useFetch } from '../../services/NetworkService';
import { useFlags } from '../../services/flags';
import { ChildrenOnly } from '../../types/ChildrenOnly';
import { asDynamic } from '../../util/Dynamic';
import { env } from '../../util/env';
import { Braze, BrazeContext } from './BrazeContext';
import { installBraze } from './installBraze.client-only';

const BRAZE_BASE_URL = 'sdk.iad-01.braze.com';

declare global {
  interface Window {
    appboy?: typeof Braze;
  }
}

export const BrazeProvider = ({ children }: ChildrenOnly) => {
  const [braze, setBraze] = useState<typeof Braze | null>(null);
  const { noBraze } = useFlags();
  const routeParams = useRouter()?.query;
  const bookingFormClient: BookingFormClient = unsafeCoerce(String(routeParams?.bookingForm));
  const bookingForm = useBookingForm(bookingFormClient);
  const { whiteLabelProps } = useTheme();
  const isCBP = whiteLabelProps?.governmentFlow ?? false;
  const user = useUserData();
  const fetch = useFetch();
  const { window } = useBrowser();

  useEffect(() => {
    if (noBraze) return;

    /** Do not load install braze for CBP */
    if (isCBP) return;

    /** Do not load install braze for widget */
    if (bookingForm === BookingForm.WIDGET) return;

    /** On the server, this will be undefined and we must bail out. */
    if (!installBraze) return;

    try {
      const braze = installBraze();

      const initialized = braze.initialize(env.BRAZE_WEB_KEY, {
        enableHtmlInAppMessages: true,
        baseUrl: BRAZE_BASE_URL,
      });

      if (!initialized) {
        throw new Error('Braze initialization returned false');
      }

      braze.display.automaticallyShowNewInAppMessages();
      braze.openSession();

      setBraze(braze);

      if (!user.loading && !user.error && user.data?.email && user.data?.mobilePhoneNumber) {
        void api3Call(
          fetch,
          api3.braze.getExternalId(user.data?.email, user.data?.mobilePhoneNumber),
        ).then(result => {
          if (result && typeof result.external_id === 'string') {
            braze.changeUser(result.external_id);
          }
        });
      }

      if (window) {
        /** Provide a global `window.appboy()` dev tool */
        window['appboy'] = braze;
      }
    } catch (cause) {
      logErrorToSentry(`Failed to initialize braze: ${cause}`, { cause: asDynamic(cause) });
    }
  }, [
    noBraze,
    bookingForm,
    isCBP,
    window,
    user.loading,
    user.error,
    user.data?.email,
    user.data?.mobilePhoneNumber,
    fetch,
  ]);

  return <BrazeContext.Provider value={braze}>{children}</BrazeContext.Provider>;
};
